// // Modified App.tsx because of react icons
// import React from "react";
// import { Navigate, Route, Routes, useLocation } from "react-router-dom";
// import { FLEETRIFYROUTEPATH } from "./routes/routesPath";
// import {
//   fleetrify_auth_route_group,
//   fleetrify_dashboard_route_group,
// } from "./routes/routes";
// import NotFoundPage from "./pages/404";
// import ProtectedRoute from "./routes/protectedRoute";
// import "./utils/echo";
// import HomePage from "../src/pages/HomePage";
// import { useChatNotifications } from "./hooks/useChatNotifications";

// // Define the route type properly to match React.FC
// type RouteProp = {
//   path: string;
//   element: React.FC;
// };

// function App() {
//   const location = useLocation();
//   useChatNotifications();
//   return (
//     <div>
//       <Routes location={location}>
//         {/* <--------- Default route -----------> */}
//         {/* <Route path="/" element={<Navigate to={FLEETRIFYROUTEPATH?.FORGOT_PASSWORD} />} /> */}

//         <Route path="/" element={<HomePage />} />
//         {/* Default redirect route */}
//         <Route path="/" element={<Navigate to={FLEETRIFYROUTEPATH?.LOGIN} />} />

//         {/* Public Auth Routes */}
//         {fleetrify_auth_route_group?.map((route: RouteProp, idx: number) => (
//           <Route key={idx} path={route.path} element={<route.element />} />
//         ))}

//         {/* Protected Dashboard Routes */}
//         <Route element={<ProtectedRoute />}>
//           {fleetrify_dashboard_route_group?.map(
//             (route: RouteProp, idx: number) => (
//               <Route key={idx} path={route.path} element={<route.element />} />
//             )
//           )}

//           {/* Catch-all route for 404 (only for authenticated users) */}
//           <Route path="*" element={<NotFoundPage />} />
//         </Route>
//       </Routes>
//     </div>
//   );
// }

// export default App;

import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { FLEETRIFYROUTEPATH } from "./routes/routesPath";
import {
  fleetrify_auth_route_group,
  fleetrify_dashboard_route_group,
} from "./routes/routes";
import NotFoundPage from "./pages/404";
import ProtectedRoute from "./routes/protectedRoute";
import PermissionGate from "./routes/PermissionGate";
import "./utils/echo";
import HomePage from "../src/pages/HomePage";
import { useChatNotifications } from "./hooks/useChatNotifications";
import { useOrderNotifications } from "./hooks/useOrderNotifications";
import { useDispatchOrderAlarm } from "./hooks/useDispatchOrderAlarm";
import InstallAppPrompt from "./components/InstallAppPrompt";
import { useAppSelector } from "./redux/services/store";
import { selectUser } from "./redux/slices/authSlice";
import { useGetCompanySubscriptionsQuery } from "./redux/services/subscription";
import Cookies from "js-cookie";

type RouteProp = {
  path: string;
  element: React.FC;
  permissions?: string[];
};

const SubscriptionNotification = () => {
  const user = useAppSelector(selectUser);
  const token = Cookies.get("token");
  const companyId = user?.company?.id || "";
  const { data: subscriptionData } = useGetCompanySubscriptionsQuery(
    { companyId, page: 1 },
    { skip: !companyId || !token }
  );

  const planName =
    subscriptionData?.subscription_plan?.name ||
    user?.company?.latestSubscription_plan?.name;

  // Only trust the subscription's own dates while it's actually active —
  // an expired/canceled subscription's next_billing_date/ends_at is a
  // leftover from its original term, not a real upcoming end date.
  const isSubscriptionActive = subscriptionData?.subscription?.status === "active";

  const expirationDateRaw = isSubscriptionActive
    ? subscriptionData?.next_billing_date ||
      subscriptionData?.subscription?.ends_at ||
      subscriptionData?.subscription?.expiration_date ||
      subscriptionData?.subscription?.expiry_date ||
      null
    : null;

  const endsOn = expirationDateRaw
    ? new Date(expirationDateRaw).toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "soon";

  const todayString = useMemo(
    () => new Date().toISOString().slice(0, 10),
    []
  );

  const [showSubscriptionCard, setShowSubscriptionCard] = useState(false);

  const positionClass = useMemo(() => {
    const positions = [
      "bottom-4 right-4 sm:bottom-6 sm:right-6",
      "bottom-4 left-4 sm:bottom-6 sm:left-6",
      "top-4 right-4 sm:top-6 sm:right-6",
      "top-4 left-4 sm:top-6 sm:left-6",
    ];
    return positions[new Date().getDate() % positions.length];
  }, []);

  useEffect(() => {
    if (!companyId || !planName || !isSubscriptionActive) return;

    const lastShownDate = localStorage.getItem("subscriptionNotificationLastShownDate");
    if (lastShownDate === todayString) return;

    const loginTimestamp = Number(
      localStorage.getItem("loginTimestamp") || String(Date.now())
    );
    const elapsed = Date.now() - loginTimestamp;
    const delay = Math.max(0, 120000 - elapsed);

    const timer = window.setTimeout(() => {
      setShowSubscriptionCard(true);
      localStorage.setItem("subscriptionNotificationLastShownDate", todayString);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [companyId, planName, todayString, isSubscriptionActive]);

  useEffect(() => {
    if (!showSubscriptionCard) return;
    const hideTimer = window.setTimeout(() => setShowSubscriptionCard(false), 60000);
    return () => window.clearTimeout(hideTimer);
  }, [showSubscriptionCard]);

  if (!showSubscriptionCard || !isSubscriptionActive) return null;

  return (
    <div
      className={`fixed z-[60] w-[calc(100vw-2rem)] max-w-xs rounded-[26px] border border-slate-200 bg-white/95 p-4 sm:p-5 shadow-[0_24px_80px_rgba(20,52,135,0.12)] backdrop-blur-xl text-sm leading-6 text-slate-900 ${positionClass}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-[#EAF3FE] text-[#1658A7]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V6l-8-4-8 4v6c0 6 8 10 8 10Z" />
            <path d="M9.5 11.5 12 14l2.5-2.5" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Subscription reminder</p>
          <p className="mt-2 text-sm text-slate-600">
            Kindly be informed you are on <span className="font-semibold text-slate-900">{planName}</span> subscription, and it will end on <span className="font-semibold text-slate-900">{endsOn}</span>.
          </p>
        </div>
        <button
          onClick={() => setShowSubscriptionCard(false)}
          className="flex-shrink-0 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close subscription notification"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

function App() {
  const location = useLocation();
  const token = Cookies.get("token");
  useChatNotifications();
  useOrderNotifications();
  useDispatchOrderAlarm();

  return (
    <div>
      {token && <SubscriptionNotification />}
      <InstallAppPrompt />
      <Routes location={location}>
        {/* Default route */}
        <Route path="/" element={<HomePage />} />

        {/* Default redirect route */}
        <Route path="/" element={<Navigate to={FLEETRIFYROUTEPATH?.LOGIN} />} />

        {/* Public Auth Routes */}
        {fleetrify_auth_route_group?.map((route: RouteProp, idx: number) => (
          <Route key={idx} path={route.path} element={<route.element />} />
        ))}

        {/* Protected Dashboard Routes */}
        <Route element={<ProtectedRoute />}>
          {fleetrify_dashboard_route_group?.map(
            (route: RouteProp, idx: number) => (
              <Route
                key={idx}
                path={route.path}
                element={
                  <PermissionGate permissions={route.permissions}>
                    <route.element />
                  </PermissionGate>
                }
              />
            )
          )}
          {/* Catch-all route for 404 (only for authenticated users) */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </div>
  );
}

export default App;